import type { ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CheckOutlined,
  DownloadOutlined,
  FileTextOutlined,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconLink,
  IconMapPin,
  IconPaperPlane,
  MailOutlined,
} from '../../components/icons'
import { Button, buttonClassName } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { useSessionWithoutProvider } from '../../lib/auth/use-session'
import { useCartCount } from '../../lib/cart/cart-count-context'
import { pluralize } from '../../lib/format/pluralize'
import { formatMoney } from './cart-money'

type CartView = components['schemas']['CartView']
/** UID-431 (ADR-0139 D5) — the real, persisted result of `POST /cart/generate-quote`,
 * replacing the old stateless `QuoteResult` (`/checkout/quote`). Deliberately carries
 * NO `summary` field (unlike the retired stub) — see the ready-state render below for
 * how the totals row is sourced instead. */
type GenerateQuoteResult = components['schemas']['ShareCartQuoteResult']
type CheckoutSummary = components['schemas']['CheckoutSummary']
/** The `Problem+JSON` shape every non-2xx `/cart/generate-quote` response shares —
 * narrowed locally only for the `type` discriminator this modal branches on
 * (ADR-0139 D5 / UX spec §4), not re-exported. */
type Problem = components['schemas']['Problem']

/** Display-only stub carriers/methods (prototype `Fictiv Platform.dc.html:4008-4009`) — the
 * Stage-1a `GenerateQuoteRequest` contract carries no shipping-company/method field, so these
 * never leave the browser; only `shipToAddressId` (below) is actually sent. */
const SHIP_COMPANIES = ['FedEx', 'UPS', 'DHL Express', 'USPS'] as const
const SHIP_METHODS = ['Priority Overnight', '2 Day', 'Ground', 'Economy'] as const

/** Display-only "recently sent to" stub suggestions for the Ready state's email field
 * (prototype `:4062-4070`) — no send actually happens; see `EMAIL_NOT_WIRED_REASON`. */
const RECENT_RECIPIENTS: ReadonlyArray<{ readonly name: string; readonly email: string }> = [
  { name: 'Maya Chen', email: 'maya.chen@northwindrobotics.com' },
  { name: 'Sam Rivera', email: 'sam.rivera@northwindrobotics.com' },
]

/** Positive helper copy under the email row (the send itself is a deliberate
 * demo simulation — a success toast standing in for the real integration,
 * same pattern as "Send to PunchOut"). */
const EMAIL_HELPER = "We'll send a copy of this quote to that address."

/** How long the "Copy link" button shows its copied checkmark/label before reverting
 * (prototype's transient `copied` state, `:4084`). */
const COPIED_RESET_MS = 2000

/** UID-430 — a floor on how long the "Generating your quote…" scanning-document
 * animation stays visible, regardless of how fast `POST /checkout/quote` actually
 * resolves. This does NOT fabricate a result — the real request still runs
 * concurrently and its outcome is unaffected — it only delays the state
 * transition so the animation is perceivable instead of flashing for a single
 * frame on a fast connection (e.g. local/staging's much lower latency than
 * production). */
export const MIN_GENERATING_MS = 1000

/** `crypto.randomUUID()` with an RFC 4122 v4 fallback for environments that lack it
 * (mirrors `MaterialsAiModal.tsx`'s / `checkout-domain.ts`'s feature-detection
 * precedent). Used to mint the client-generated idempotency key `POST
 * /cart/generate-quote` requires (ADR-0139 D3). */
function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** UID-431 (UX spec §4) — the one Problem `type` this modal branches on for specific
 * copy, rather than falling through to the generic fallback. Retrying an unpriced
 * cart without changing anything fails identically, so the copy tells the buyer to
 * leave the modal and fix their cart instead of just "try again". */
const NOT_FULLY_PRICED_MESSAGE =
  'Some items in your cart still need pricing before you can generate a quote. Review your cart and try again.'

/** Resolves the user-facing error copy for a failed `POST /cart/generate-quote`,
 * branching on the Problem's stable `type` discriminator (never string-matching
 * `detail`) per ADR-0139 D5 / UX spec §4. `cart/empty` and any other Problem type
 * fall through to the existing generic fallback — this endpoint's own precondition
 * copy only needs to special-case the "not fully priced" case, since `cart/empty`
 * shouldn't be reachable from this modal (it only opens over a non-empty cart) but
 * is handled safely by the same fallback if it ever is. */
function resolveGenerateQuoteErrorMessage(error: Problem | undefined): string {
  if (error?.type === 'cart/not-fully-priced') return NOT_FULLY_PRICED_MESSAGE
  return error?.detail ?? 'Could not generate a quote. Please try again.'
}

/**
 * A COMPACT, single-line display form of a full `quoteRef` (e.g.
 * `"Q-2902be8f-8ddc-49ba-b3b7-e3caf71288fa"` → `"Q-2902BE8F"`) — the full 36-char UUID wraps
 * across ~4 lines in the card's fixed-width slot and overflows it (a live-verification defect).
 * Takes just the UUID's first segment (8 hex chars), uppercased, matching the prototype's
 * short quote-id treatment. Falls back to the input unchanged if it doesn't look like the
 * expected `"Q-<uuid>"` shape, rather than mangling something it doesn't recognize.
 *
 * UID-431: retained (still exported + unit-tested) but no longer called from the ready-card
 * render. It shortened the OLD stub's raw `quoteRef` UUID; the real, persisted
 * `quoteNumber` this modal now shows (e.g. `"RFQ-2026-0001"`, server-derived via
 * `formatRfqNumber`) is already a short, display-ready string with no truncation risk, so
 * there is nothing left for this helper to shorten on that render path.
 */
export function formatCompactQuoteRef(quoteRef: string): string {
  const withoutPrefix = quoteRef.startsWith('Q-') ? quoteRef.slice(2) : quoteRef
  const firstSegment = withoutPrefix.split('-')[0]
  if (!firstSegment) return quoteRef
  return `Q-${firstSegment.toUpperCase()}`
}

type ViewState = 'confirm' | 'generating' | 'ready' | 'error'

export interface GenerateQuoteModalProps {
  readonly cart: CartView
  readonly open: boolean
  readonly onClose: () => void
  /** Reused verbatim from CartPage's own "Continue to checkout" handler (UID-131 follow-up)
   * — the Ready state's "Proceed to checkout" button is the SAME navigation, just reachable
   * from inside this modal too; it closes the modal first so the buyer doesn't land on
   * checkout with a stale generate-quote dialog still mounted underneath. */
  readonly onProceedToCheckout: () => void
  /** Ready-state primary CTA label. Defaults to "Proceed to checkout" (the cart
   * context). Checkout passes "Done" — the buyer is already on checkout, so the
   * button just closes the modal rather than mislabeling a no-op navigation. */
  readonly proceedLabel?: string
  /**
   * UID-403 — whether the Ready state's proceed CTA may actually navigate to
   * checkout. Defaults to `true` (permissive) BECAUSE `CheckoutPage` reuses this
   * modal with `proceedLabel="Done"` and an `onProceedToCheckout` that only
   * closes it: that no-op close must never be disabled, and checkout has no
   * cart-readiness verdict to offer. `OrderSummaryRail` opts in by passing
   * `resolveCheckoutReadiness(cart).canCheckout` — the SAME verdict gating its
   * own "Continue to checkout" — closing the hole where this modal was a second,
   * ungated route to `/checkout` that CheckoutPage then bounced back to `/cart`.
   */
  readonly canProceedToCheckout?: boolean
  /** Why proceeding is blocked, shown beside the disabled CTA so it is never a
   * dead button with no explanation. Ignored when `canProceedToCheckout`.
   * Explicitly `| undefined` because this package runs with
   * `exactOptionalPropertyTypes`, and callers forward an already-optional
   * `resolveCheckoutReadiness().reason` straight through. */
  readonly proceedBlockedReason?: string | undefined
  /**
   * UID-431 (ADR-0139 D5 / UX spec §3) — called once, when the modal is dismissed
   * (Continue shopping / header close / Escape / backdrop, or the footer's proceed
   * CTA) AFTER a successful generate-quote. `POST /cart/generate-quote` clears the
   * cart server-side but deliberately does NOT inline the resulting empty cart in
   * its response — the caller re-GETs `/cart` here to drive its own empty-state
   * transition, and is the right place to fire a "Cart cleared — quote generated"
   * toast. Never called for a close from the confirm/error states (nothing to
   * refresh) or from "Open priced RFQ" (a real navigation that unmounts this modal
   * without going through `onClose` at all — see that control's own doc comment).
   * Optional/no-op default: `CheckoutPage` reuses this modal with no cart-empty-state
   * concern of its own and doesn't need to wire anything.
   */
  readonly onQuoteGenerated?: (() => void) | undefined
  /**
   * UID-431 (ADR-0141 D3) — the originating RFQ to REUSE on share, when this modal is opened
   * from a surface that already holds a `quoted`/`accepted` RFQ. Present ⇒ the share endpoint
   * reuses it (no duplicate); absent ⇒ it creates a fresh RFQ from the cart. Cart surfaces omit it.
   */
  readonly originatingRfqId?: string | undefined
  /**
   * UID-431 — the confirm-state header. Defaults to the cart's "Generate quote" copy; the
   * RFQ share surface passes "Share quote" + its own subtitle (Image: Share quote modal).
   */
  readonly title?: string
  readonly subtitle?: ReactNode
  /**
   * UID-431 — when true, the ready ("Quote generated") state shows a single full-width
   * "Done" button that just closes the modal, instead of Continue-shopping + Proceed-to-
   * checkout. Used by the RFQ share surface (the buyer stays where they were on Done).
   */
  readonly readyDoneOnly?: boolean
}

/**
 * The Cart screen's "Generate quote" modal (UID-131, prototype `Fictiv Platform.dc.html:3989-4090`;
 * UID-431/ADR-0139 reworked the actual generate call): confirm the ship-to + a display-only
 * carrier/method pair → POST `/cart/generate-quote` → a real, persisted quote number + the
 * account-scoped `/rfq/:rfqId` deep link. Three states in one shell (Modal already owns
 * focus-trap/Escape/backdrop-close):
 *  - **confirm**: ship-to summary, stub shipping selects, Cancel/Generate.
 *  - **generating**: a brief loading state (scanning-document animation, UID-430) while
 *    the POST is in flight.
 *  - **ready**: the returned `quoteNumber` + link, alongside the SAME reconciled totals the
 *    confirm state showed (see `quotePreview`'s doc — the new endpoint's response carries no
 *    summary of its own), plus a display-only "email this quote" stub (no send endpoint exists
 *    — honestly disabled, not silently broken) and Continue-shopping/Proceed/Open-RFQ controls.
 * A failed POST shows an inline error (UID-431: specific copy for `cart/not-fully-priced`, see
 * `resolveGenerateQuoteErrorMessage`) and returns to **confirm** so the buyer can retry without
 * losing their (stub) carrier/method picks — reusing the SAME idempotency key (ADR-0139 D3), so
 * a retry can't double-generate.
 *
 * TOTAL-RECONCILIATION FIX (UID-131 live-verification defect): the Confirm state's "Quote
 * summary" is deliberately NOT `cart.summary`. Investigated whether the cart's selected
 * delivery option (`CartView.shipPlan.selectedDeliveryOptionId`, e.g. `"do-expedited"`,
 * EXPEDITED/STANDARD/OVERSEAS, `apps/api/.../cart/cart-derivation.ts`) could be passed as
 * `GenerateQuoteRequest.shippingMethodId` so the quote's shipping line matches the cart's — it
 * cannot: `POST /checkout/quote` and `GET /checkout` resolve `shippingMethodId` against a
 * COMPLETELY SEPARATE demo rate table (`ship-overnight` $59.36 / `ship-ground` $16.64 /
 * `ship-own-account` $0.00, `apps/api/.../checkout/checkout-derivation.ts`
 * `DEMO_SHIPPING_METHODS`) with NO shared id, NO shared price, and no documented mapping to the
 * cart's EXPEDITED/STANDARD/OVERSEAS delivery options — passing `"do-expedited"` as
 * `shippingMethodId` would just 422 (`Unknown shipping method`). Inventing a client-side
 * id-guess mapping between two independently-fixtured demo tables would be worse than the bug
 * (a confident-looking but fabricated correspondence). So instead: this modal fetches `GET
 * /checkout` (the SAME `presentCheckout` derivation `POST /checkout/quote` uses, with the SAME
 * `shipToAddressId` and no `shippingMethodId` on both calls) and shows THAT summary in Confirm,
 * not the cart's own summary — guaranteeing Confirm and Ready always show the identical total,
 * even though neither reflects the cart's actual EXPEDITED shipping choice. FLAGGED: this is a
 * real product/architecture gap (the checkout shipping-method table has no path back to the
 * cart's chosen delivery option) that a durable fix needs to close upstream, not a client
 * workaround this modal can fully solve.
 */
export function GenerateQuoteModal({
  cart,
  open,
  onClose,
  onProceedToCheckout,
  proceedLabel = 'Proceed to checkout',
  canProceedToCheckout = true,
  proceedBlockedReason,
  onQuoteGenerated,
  originatingRfqId,
  title = 'Generate quote',
  subtitle,
  readyDoneOnly = false,
}: GenerateQuoteModalProps) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const session = useSessionWithoutProvider()
  const [viewState, setViewState] = useState<ViewState>('confirm')
  const [shipCompany, setShipCompany] = useState<string>(SHIP_COMPANIES[0])
  const [shipMethod, setShipMethod] = useState<string>(SHIP_METHODS[0])
  const [errorMessage, setErrorMessage] = useState<string>()
  const [quote, setQuote] = useState<GenerateQuoteResult>()
  const [email, setEmail] = useState('')
  const [emailMenuOpen, setEmailMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  // The Confirm-state "Quote summary" — fetched from the SAME `GET /checkout` derivation
  // `POST /checkout/quote` used to (see the module doc's TOTAL-RECONCILIATION FIX), never
  // `cart.summary`. `undefined` = still loading; `previewError` set = the fetch failed
  // (Generate is disabled either way — a buyer must never confirm against a total that
  // hasn't actually been confirmed as correct). UID-431: `POST /cart/generate-quote`'s own
  // response carries NO summary/totals field at all (ADR-0139 D5 — only
  // `{rfqId, quoteNumber, quoteState, rfqUrl}`), so this same reconciled preview now ALSO
  // sources the ready-card's itemCount/total (below) — the last-known-good total the buyer
  // confirmed against, which is exactly what was just quoted. FLAGGED to software engineer/PM:
  // a durable fix would have the endpoint echo back the frozen quote's own totals rather than
  // the FE reusing its pre-generate preview.
  const [quotePreview, setQuotePreview] = useState<CheckoutSummary>()
  const [previewError, setPreviewError] = useState<string>()
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // UID-431 (ADR-0139 D3) — the client-generated idempotency key for the CURRENT
  // generate-quote attempt. Minted once per modal open (below) and reused, unchanged,
  // across every retry of that same attempt (a re-click of "Generate quote" after an
  // error) so a retry can't double-generate; a fresh open (a NEW intent) mints a new one.
  const idempotencyKeyRef = useRef<string>('')
  // UID-431 (UX spec §5 focus management) — on entering 'ready', focus moves here (the
  // quote-number heading) so a screen-reader user who triggered generation is told new
  // content appeared, instead of staying focused on the (now-removed) Generate button.
  const quoteHeadingRef = useRef<HTMLParagraphElement>(null)
  /**
   * UID-403 — the "Recently sent to" dropdown had no dismissal path at all: it
   * opened on input focus and closed ONLY when a suggestion was clicked, so a
   * buyer who opened it and changed their mind was stuck with it covering the
   * dialog. (UID-410's fix for it being CLIPPED by the modal body is a different
   * concern and is untouched here.) This ref scopes the outside-click test to the
   * input + menu subtree.
   */
  const emailMenuWrapRef = useRef<HTMLDivElement | null>(null)

  /**
   * UID-403 — dismiss the share dropdown on an outside pointer-down. Bound only
   * while the menu is actually open, so there is no idle document listener.
   * `pointerdown` (not `click`) so the menu closes on press, before a click on
   * the element underneath resolves. A press INSIDE the wrap (the input or a
   * suggestion) is ignored so the menu's own controls keep working.
   */
  useEffect(() => {
    if (!emailMenuOpen) return
    function handlePointerDown(event: PointerEvent) {
      const wrap = emailMenuWrapRef.current
      if (wrap && event.target instanceof Node && !wrap.contains(event.target)) {
        setEmailMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [emailMenuOpen])
  const shipCompanyId = useId()
  const shipMethodId = useId()
  const emailId = useId()
  const emailNoteId = useId()

  const { summary } = cart
  const selectedAddress = cart.shipToAddresses.find(
    (address) => address.id === summary.shipToAddressId,
  )

  async function loadQuotePreview() {
    setQuotePreview(undefined)
    setPreviewError(undefined)
    try {
      const { data, error } = await apiClient.GET('/checkout', {
        params: { query: { shipToAddressId: summary.shipToAddressId } },
      })
      if (data) {
        setQuotePreview(data.summary)
        return
      }
      setPreviewError(error?.detail ?? 'Could not load the quote summary. Please try again.')
    } catch {
      setPreviewError('Could not load the quote summary. Please try again.')
    }
  }

  // Reset to a clean confirm state every time the modal is (re-)opened — a previous
  // generate/error/ready run must never bleed into the next open — and load the
  // reconciled preview summary fresh each time (the cart may have changed since last open).
  // Intentionally re-runs only on `open` (a fresh mount of the confirm flow):
  // `loadQuotePreview` reads `summary`/`cart` at fire time, not tracked as a re-trigger, since
  // the buyer changes the ship-to from the CART page (closing this modal), not from inside it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    if (!open) return
    setViewState('confirm')
    setShipCompany(SHIP_COMPANIES[0])
    setShipMethod(SHIP_METHODS[0])
    setErrorMessage(undefined)
    setQuote(undefined)
    setEmail('')
    setEmailMenuOpen(false)
    setCopied(false)
    // UID-431 (ADR-0139 D3) — a fresh open is a NEW generate-quote intent: mint a new
    // idempotency key. `handleGenerate` (below) reads this ref rather than minting its
    // own, so re-clicking "Generate quote" after an error reuses the SAME key.
    idempotencyKeyRef.current = generateUuidV4()
    void loadQuotePreview()
  }, [open])

  // A pending "Copied!" revert must never fire after this component (or the copy-link
  // affordance it belongs to) is gone — clear it on unmount, same discipline as any other
  // setTimeout side effect.
  useEffect(() => {
    return () => clearTimeout(copiedTimeoutRef.current)
  }, [])

  // UID-431 (UX spec §5) — move focus to the quote-number heading on entering 'ready',
  // rather than leaving it on the (now-removed) "Generate quote" button with no
  // announcement that new content appeared. The three view-states share ONE `Modal`
  // instance (see the module doc), so this can't ride on Modal's own mount-focus effect.
  useEffect(() => {
    if (viewState === 'ready') quoteHeadingRef.current?.focus()
  }, [viewState])

  /** UID-431 (ADR-0139) — closes the modal, first handing back the "a quote was just
   * generated" signal (`onQuoteGenerated`) so the caller can re-GET `/cart` and show the
   * "Cart cleared — quote generated" toast (UX spec §3). Gated on actually being in the
   * ready state with a real result, so a Cancel/Escape from confirm or error never fires
   * it. Every dismissal path in this component (Cancel, the ready footer's two buttons,
   * `Modal`'s own header-close/Escape/backdrop) routes through this one function so there
   * is exactly one place that decides whether a generate-quote succeeded. */
  function handleModalClose() {
    if (viewState === 'ready' && quote) {
      // UID-431: a successful CREATE-branch generate cleared the cart server-side, so the badge
      // must drop to 0 on ANY dismissal of the ready modal — the header-close/Escape/backdrop,
      // "Open priced RFQ", Done, AND the Continue-shopping/Proceed CTAs all funnel through here,
      // which is the one place that reliably covers every exit (previously only the two CTAs
      // zeroed it, leaving a stale badge when the buyer closed via X/Escape/Open-RFQ). Still not
      // "before loading the modal" — it fires only on the dismissal action. The REUSE branch
      // (originatingRfqId present, RFQ share surface) leaves the cart intact, so it must NOT zero.
      if (originatingRfqId === undefined) setLineCount(0)
      onQuoteGenerated?.()
    }
    onClose()
  }

  async function handleGenerate() {
    setViewState('generating')
    setErrorMessage(undefined)
    const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_GENERATING_MS))
    try {
      const [{ data, error }] = await Promise.all([
        apiClient.POST('/cart/share-quote', {
          body: {
            idempotencyKey: idempotencyKeyRef.current,
            ...(originatingRfqId !== undefined ? { originatingRfqId } : {}),
          },
        }),
        minDelay,
      ])
      if (data) {
        setQuote(data)
        // UID-431: the cart was cleared server-side, but the badge is NOT zeroed here — it
        // stays at its current value while the "Quote generated" modal is open, and drops to 0
        // only when the buyer acts on a ready-state CTA (Proceed to checkout / Continue
        // shopping), per the requirement "clear on the action, not on modal load".
        setViewState('ready')
        return
      }
      setErrorMessage(resolveGenerateQuoteErrorMessage(error))
      setViewState('error')
    } catch {
      await minDelay
      setErrorMessage('Something went wrong. Please try again.')
      setViewState('error')
    }
  }

  /** UID-431 (ADR-0141, decision A): copies the link to the read-only priced RFQ page,
   * built on THIS SITE's own origin (`window.location.origin`) + `/rfq/{rfqId}` — the same
   * account-scoped deep link "Open priced RFQ" navigates to, never the backend's hardcoded
   * share-base domain. Feature-detects `navigator.clipboard`; an unavailable/denied clipboard
   * surfaces a visible toast fallback (not a silent no-op). */
  async function handleCopyLink() {
    if (!quote) return
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable')
      }
      const absoluteUrl = `${window.location.origin}/rfq/${quote.rfqId}`
      await navigator.clipboard.writeText(absoluteUrl)
      setCopied(true)
      clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
    } catch {
      // Clipboard permission denied/unavailable/thrown — no crash. UX spec §1a: a
      // visible fallback (not a silent no-op) since the copy target is now a real,
      // useful URL — consistent with `ShareHandoffModal`'s clipboard-failure pattern.
      showToast(
        "Couldn't copy the link automatically — select the link and copy it manually.",
        'error',
      )
    }
  }

  /** Emailing the quote is a deliberate demo simulation (no send endpoint in the
   * Stage-1a contract) — a real, enabled action that fires a success toast
   * standing in for the integration, same pattern as "Send to PunchOut". */
  function handleSendQuote() {
    const to = email.trim()
    if (!to) {
      showToast('Enter an email address to send this quote to.', 'info')
      return
    }
    showToast(`Quote sent to ${to}`)
    setEmail('')
    setEmailMenuOpen(false)
  }

  /** PDF export is a demo simulation (no PDF-generation endpoint) — a positive
   * success toast rather than a "not available" dead-end. */
  function handleDownloadPdf() {
    showToast('Quote PDF downloaded')
  }

  /** REAL: closes the modal and navigates away to the catalog, same as any other
   * "keep shopping" exit from the cart. Routes through `handleModalClose` (not the
   * raw `onClose` prop) so a successful generate-quote's cart-refresh/toast fires. */
  function handleContinueShopping() {
    // UID-431: badge-zeroing happens in `handleModalClose` (the single dismissal funnel, gated
    // to the CREATE branch) — synchronously, before this navigates away.
    handleModalClose()
    navigate('/catalog')
  }

  /** REAL: closes the modal, then reuses CartPage's own "Continue to checkout" navigation —
   * the SAME destination the rail's primary CTA goes to, just reachable from in here too. */
  function handleProceedToCheckoutFromModal() {
    // UID-403 — belt-and-braces alongside the `disabled` attribute: never close
    // the modal or fire the navigation when the cart isn't checkout-ready, so a
    // programmatic/synthetic click can't reopen the empty-cart bounce either.
    if (!canProceedToCheckout) return
    // UID-431: badge-zeroing happens in `handleModalClose` (the single dismissal funnel, gated
    // to the CREATE branch) — synchronously, before the `onProceedToCheckout` navigation.
    handleModalClose()
    onProceedToCheckout()
  }

  if (viewState === 'generating') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Generating your quote…"
        maxWidth={540}
        hideHeader
        className="generate-quote-modal-generating"
      >
        {/* UID-430 — the real "scanning document" animation (`Fictiv Platform.dc.html:
            4996-5010`), replacing a generic `Spinner`. Purely decorative (see the
            `prefers-reduced-motion` neutralization in cart.css) — the heading/detail
            text below is the actual accessible status. */}
        {/* `<output>` carries an implicit `role="status"` (biome's
            `useSemanticElements`) — no explicit role needed. */}
        <output className="generate-quote-generating">
          <div className="qg-doc" aria-hidden="true">
            <div className="qg-scan" />
            <div className="qg-line" style={{ top: 16, width: 52, animationDelay: '0s' }} />
            <div className="qg-line" style={{ top: 30, width: 40, animationDelay: '.12s' }} />
            <div className="qg-line" style={{ top: 44, width: 52, animationDelay: '.24s' }} />
            <div className="qg-line" style={{ top: 58, width: 34, animationDelay: '.36s' }} />
            <div
              className="qg-line"
              style={{
                top: 76,
                width: 46,
                height: 9,
                background: 'var(--color-brand-tint-100)',
                animationDelay: '.48s',
              }}
            />
          </div>
          <p className="generate-quote-generating-heading">Generating your quote…</p>
          <p className="generate-quote-generating-detail">
            Pricing {summary.lineCount} item{summary.lineCount === 1 ? '' : 's'} and locking in your
            lead times
          </p>
          <div className="generate-quote-generating-progress" aria-hidden="true">
            <div className="generate-quote-generating-progress-fill" />
          </div>
        </output>
      </Modal>
    )
  }

  if (viewState === 'ready' && quote) {
    return (
      <Modal
        open={open}
        onClose={handleModalClose}
        title="Quote generated"
        maxWidth={540}
        // UID-410 AC5: the prototype's Ready state has NO header bar at all
        // (`Fictiv Platform.dc.html:5011-5017`) — the body already draws its
        // own centered check-icon + heading below. Rendering the shared
        // `Modal` header too doubled the "Quote generated" heading.
        hideHeader
        // UID-410 AC4: scopes `.modal-body { overflow: visible }` to JUST
        // this Ready-state instance (see cart.css) so the "Recently sent
        // to" email menu can float past the body's box instead of being
        // clipped — the prototype's own Ready-state scroll region is
        // `overflow:visible` for the same reason (`dc.html:5017`). Every
        // OTHER modal keeps the shared `.modal-body { overflow-y: auto }`.
        className="generate-quote-modal-ready"
        footer={
          readyDoneOnly ? (
            // RFQ share surface: a single full-width "Done" that just closes the modal
            // (the buyer stays on the RFQ page). See the "Quote generated" share screen.
            <Button
              variant="primary"
              size="xl"
              className="generate-quote-done-btn"
              onClick={handleModalClose}
            >
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="xl" onClick={handleContinueShopping}>
                Continue shopping
              </Button>
              <Button
                variant="primary"
                size="xl"
                disabled={!canProceedToCheckout}
                onClick={handleProceedToCheckoutFromModal}
              >
                {proceedLabel}
              </Button>
              {!canProceedToCheckout && proceedBlockedReason && (
                <p className="caption-text generate-quote-proceed-blocked">
                  {proceedBlockedReason}
                </p>
              )}
            </>
          )
        }
      >
        <div className="generate-quote-ready">
          <span className="generate-quote-ready-icon" aria-hidden="true">
            <IconCheck />
          </span>
          {/* UID-410 AC5: the body's own heading (prototype dc.html:5013),
              now that the shared Modal header bar is suppressed for this
              state (`hideHeader`) instead of doubling this text. */}
          <p className="generate-quote-ready-heading">Quote generated</p>
          <p className="generate-quote-ready-detail">
            A confirmation with the PDF quotation has been emailed to {session?.email}.
          </p>
        </div>
        <div className="generate-quote-card">
          <div className="generate-quote-card-head">
            <div>
              <p className="generate-quote-card-eyebrow">Quote number</p>
              {/* UID-431: the REAL, persisted quote number (`formatRfqNumber`, e.g.
                  "RFQ-2026-0001") — replaces the old stub's ephemeral client-minted
                  `quoteRef`. Already a short, display-ready string (no 36-char UUID to
                  truncate), but kept `tabIndex={-1}` + a `title` for parity with the
                  rest of this card's truncation convention and as the UX spec's §5
                  focus target on entering this state. */}
              <p
                ref={quoteHeadingRef}
                className="generate-quote-card-ref"
                title={quote.quoteNumber}
                tabIndex={-1}
              >
                {quote.quoteNumber}
              </p>
            </div>
            <div className="generate-quote-card-totals">
              {/* UID-419: render the cart's server-derived `arrivalEstimateLabel`
                  VERBATIM — never re-derive/truncate it client-side. It already
                  carries the correct shipment count ("One shipment" / "N
                  shipments"); a prior version of this line regex-stripped
                  everything after the first "·" to shorten it, which silently
                  dropped that count on a multi-shipment order. See the same
                  convention enforced in `OrderSummaryRail.tsx` / its
                  "live-walkthrough regression" test.

                  UID-431: `itemCount`/`totalAmount` now source from `quotePreview`
                  (the SAME reconciled `GET /checkout` summary the confirm state
                  showed) rather than a response `summary` field — `POST
                  /cart/generate-quote`'s result carries NO summary at all
                  (ADR-0139 D5), unlike the retired stub this replaces. See this
                  component's `quotePreview` state doc for why that's the correct
                  source, and the FLAGGED note there for the upstream gap.
                  `summary.arrivalEstimateLabel` below is still `cart.summary` (a
                  pre-existing, separately FLAGGED mismatch — see the same doc
                  comment history predating UID-431 — left as-is here since this
                  fix only closes the gap the new endpoint actually created). */}
              <p className="caption-text">
                {quotePreview?.itemCount ?? 0} {pluralize(quotePreview?.itemCount ?? 0, 'item')}
                {summary.arrivalEstimateLabel ? ` · ${summary.arrivalEstimateLabel}` : ''}
              </p>
              <p className="generate-quote-card-total">
                {formatMoney(quotePreview?.totalAmount ?? '0.00', quotePreview?.currency ?? 'USD')}
              </p>
            </div>
          </div>
          <div className="generate-quote-email-row">
            <label htmlFor={emailId} className="generate-quote-eyebrow-label">
              Email this quote
            </label>
            {/* Prototype `:4076` — input + Send on ONE row (input flex:1, ~9px gap), not
                stacked. The input-wrap needs `min-width: 0` to actually shrink inside a flex
                row instead of forcing the row to overflow. */}
            <div className="generate-quote-email-input-row">
              {/* UID-403 — `onKeyDown` here (capturing the whole wrap, input +
                  menu) so Escape closes just the DROPDOWN and stops the event
                  before Modal's own Escape handler closes the entire dialog and
                  loses the buyer's generated quote. Escape with the menu already
                  shut falls through to Modal as normal. */}
              <div
                className="generate-quote-email-input-wrap"
                ref={emailMenuWrapRef}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && emailMenuOpen) {
                    event.stopPropagation()
                    setEmailMenuOpen(false)
                  }
                }}
              >
                <span className="generate-quote-email-icon" aria-hidden="true">
                  <MailOutlined />
                </span>
                <input
                  id={emailId}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onFocus={() => setEmailMenuOpen(true)}
                  placeholder="Enter or pick an email address…"
                  aria-describedby={emailNoteId}
                  className="generate-quote-email-input"
                />
                {emailMenuOpen && (
                  <div className="generate-quote-email-menu" role="menu">
                    <p className="generate-quote-email-menu-heading">Recently sent to</p>
                    {RECENT_RECIPIENTS.map((recipient) => (
                      <button
                        key={recipient.email}
                        type="button"
                        role="menuitem"
                        className="generate-quote-email-suggestion"
                        onClick={() => {
                          setEmail(recipient.email)
                          setEmailMenuOpen(false)
                        }}
                      >
                        <span className="generate-quote-email-suggestion-name">
                          {recipient.name}
                        </span>
                        <span className="generate-quote-email-suggestion-address">
                          {recipient.email}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Demo simulation: a real, enabled action that fires a success
                  toast, same pattern as "Send to PunchOut". */}
              <Button variant="secondary" aria-describedby={emailNoteId} onClick={handleSendQuote}>
                <IconPaperPlane />
                Send
              </Button>
            </div>
          </div>
          <p id={emailNoteId} className="caption-text generate-quote-email-note">
            {EMAIL_HELPER}
          </p>
        </div>

        <div className="generate-quote-actions-row">
          {/* Demo simulation (no PDF-generation endpoint) — a positive success
              toast rather than a disabled "not available" stub. */}
          <Button variant="secondary" size="xl" onClick={handleDownloadPdf}>
            <DownloadOutlined aria-hidden="true" />
            Download quote (PDF)
          </Button>
          {/* UID-431 (UX spec §1a): relabelled "Copy quote link" (was "Copy shareable
              link") — distinct from `ShareHandoffModal`'s "Copy link" wording, so the
              two surfaces don't read as identical features (see this file's module doc
              cross-flow finding). Copies a REAL absolute URL now (`handleCopyLink`),
              fixing the pre-existing bug where this copied a bare, unresolvable ref.
              `aria-label` sets an explicit accessible name per state (the visible text
              already changes, but a screen-reader user tabbing here before activating
              it should get a name that doesn't presuppose success); the `aria-live`
              span below is the actual announcement path for a user whose focus stays on
              this button after the click, since NVDA/VoiceOver don't reliably re-announce
              an `aria-label` change on an already-focused element. */}
          <Button
            variant="secondary"
            size="xl"
            aria-label={copied ? 'Quote link copied to clipboard' : 'Copy quote link'}
            onClick={() => void handleCopyLink()}
          >
            {copied ? <CheckOutlined aria-hidden="true" /> : <IconLink aria-hidden="true" />}
            {copied ? 'Copied!' : 'Copy quote link'}
          </Button>
          <span className="sr-only" aria-live="polite">
            {copied ? 'Quote link copied to clipboard' : ''}
          </span>
        </div>

        {/* UID-431 (UX spec §2) — its own full-width row, visually distinct from the
            two secondary actions above (not a third item crammed into that row), so it
            reads as the primary next step for a buyer who wants to inspect the RFQ
            rather than just walk away with a copied link. A REAL anchor/router `Link`
            (not a `<button onClick={() => navigate(...)}>` faking navigation — WCAG:
            native "link" role, Enter-operable, right-click "open in new tab", Cmd+click)
            targeting the relative `rfqUrl` the response returned. `variant="secondary"`
            — NOT `"primary"`: the footer's "Proceed to…" stays this modal's one
            primary-emphasis CTA (single-primary-action convention already in use here).
            Navigating away unmounts this modal; `Modal`'s own focus-restore effect and
            the cart empty-state transition need no extra code for this path (see this
            control's UX spec §2/§3 for why), so it deliberately does NOT go through
            `handleModalClose`/`onQuoteGenerated` — the whole page is about to change. */}
        <div className="generate-quote-open-rfq-row">
          <Link
            to={`/rfq/${quote.rfqId}`}
            className={buttonClassName(
              { variant: 'secondary', size: 'xl' },
              'generate-quote-open-rfq',
            )}
            // Close the modal on navigation so the RFQ page is revealed — otherwise, when this
            // modal is opened FROM the RFQ page (same `/rfq/:id` route), the router treats the
            // click as a no-op and the modal stays overlaid (UID-431).
            onClick={handleModalClose}
          >
            Open priced RFQ
            <IconArrowRight aria-hidden="true" />
          </Link>
        </div>
      </Modal>
    )
  }

  // confirm + error share the same shell — an error is an inline banner on top of the
  // otherwise-unchanged confirm state, so the buyer's carrier/method picks survive a retry.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      // UID-430: the design's confirm-state header is title + a subtitle
      // directly under it, in ONE header block (`Fictiv Platform.dc.html:
      // 4966-4969`) — rendered via `Modal`'s own `subtitle` slot rather than
      // as a body paragraph (see the retired `.generate-quote-subtext`'s
      // comment in cart.css). UID-431: the RFQ share surface overrides both
      // `title` and `subtitle`; the cart keeps the default copy below.
      subtitle={
        subtitle ??
        'Everything’s priced — confirm the details below and we’ll generate a formal quotation.'
      }
      maxWidth={540}
      className="generate-quote-modal-confirm"
      footer={
        <>
          <Button variant="secondary" size="xl" onClick={onClose}>
            Cancel
          </Button>
          {/* Disabled until the reconciled preview total has loaded — generating a quote
              before then would risk a fresh mismatch, exactly the defect this fixes. */}
          <Button
            variant="primary"
            size="xl"
            className="generate-quote-generate-btn"
            disabled={!quotePreview}
            onClick={() => void handleGenerate()}
          >
            <FileTextOutlined aria-hidden="true" />
            Generate quote
          </Button>
        </>
      }
    >
      {viewState === 'error' && errorMessage && (
        <div role="alert" className="generate-quote-error">
          <p className="form-error-text">{errorMessage}</p>
        </div>
      )}

      <p className="generate-quote-section-label">Ship to</p>
      <div className="generate-quote-ship-to-card">
        <span className="generate-quote-ship-to-icon" aria-hidden="true">
          <IconMapPin />
        </span>
        <div className="generate-quote-ship-to-body">
          <p className="generate-quote-ship-to-name">
            {selectedAddress?.label ?? 'Ship-to address'}
          </p>
          {selectedAddress && (
            <p className="generate-quote-ship-to-line">
              {selectedAddress.line1}
              {selectedAddress.line2 ? `, ${selectedAddress.line2}` : ''}, {selectedAddress.city},{' '}
              {selectedAddress.region} {selectedAddress.postalCode}
            </p>
          )}
        </div>
        {/* Display-only: closes back to the cart's own ship-to select rather than re-picking
            here — there is no second, in-modal address-selection flow (per the ticket). */}
        <button type="button" className="generate-quote-ship-to-change" onClick={onClose}>
          Change
        </button>
      </div>

      <div className="generate-quote-shipping-grid">
        <div className="form-field">
          <label htmlFor={shipCompanyId}>Shipping company</label>
          <select
            id={shipCompanyId}
            value={shipCompany}
            onChange={(event) => setShipCompany(event.target.value)}
          >
            {SHIP_COMPANIES.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor={shipMethodId}>Shipping method</label>
          <select
            id={shipMethodId}
            value={shipMethod}
            onChange={(event) => setShipMethod(event.target.value)}
          >
            {SHIP_METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="generate-quote-section-label">Quote summary</p>
      {/* Sourced from `quotePreview` (GET /checkout's summary) — NEVER `cart.summary` — so
          this total is GUARANTEED to match what "Generate quote" is about to return. See the
          module doc's TOTAL-RECONCILIATION FIX for why the cart's own summary can't be used
          here (its shipping line reflects a delivery option the quote endpoint has no way to
          honor). No numbers render until this has loaded, so the buyer never sees one total
          here and a different one on the next screen. */}
      {previewError && (
        <div role="alert" className="generate-quote-error">
          <p className="form-error-text">{previewError}</p>
          <button type="button" className="btn-link" onClick={() => void loadQuotePreview()}>
            Retry
          </button>
        </div>
      )}
      {!previewError && !quotePreview && (
        <div className="generate-quote-summary-loading">
          <Spinner size="sm" label="Loading quote summary" />
        </div>
      )}
      {quotePreview && (
        <>
          <dl className="cart-summary-totals generate-quote-summary-totals">
            <dt>
              {/* UID-410 AC5: singular/plural agreement by count (was hardcoded "items"). */}
              Part production ({quotePreview.itemCount} {pluralize(quotePreview.itemCount, 'item')})
            </dt>
            {/* UID-430/ADR-0131 D6: `quotePreview.subtotalAmount` is checkout's NET
                subtotal (parts − corporate discount + charges,
                apps/api's checkout-derivation.ts `deriveTaxedMoney`) — deliberately, so
                Subtotal + Shipping + Tax = Total foots ON SCREEN with no separate
                discount line, matching `CheckoutSummaryRail`'s own display. An earlier
                version of this fix added an explicit "Corporate discount" line computed
                from `cart.summary` (the CART page's own GROSS-subtotal shape) — that
                would double-subtract the discount now that checkout's own subtotal is
                already net. Do not reintroduce it here; the cart page's summary
                (`OrderSummaryRail.tsx`) is the one surface that still shows the discount
                as its own line, because IT keeps a gross subtotal by design. */}
            <dd>{formatMoney(quotePreview.subtotalAmount, quotePreview.currency)}</dd>
            <dt>Shipping</dt>
            <dd>{formatMoney(quotePreview.shippingAmount, quotePreview.currency)}</dd>
            <dt>Tax</dt>
            <dd>{formatMoney(quotePreview.taxAmount, quotePreview.currency)}</dd>
            <dt className="generate-quote-total-label">Quote total</dt>
            <dd className="generate-quote-total-value">
              {formatMoney(quotePreview.totalAmount, quotePreview.currency)}
            </dd>
          </dl>
          <p className="generate-quote-lock-note">
            {/* UID-419: clock glyph before the lock note, verbatim path data from
                the prototype (`Fictiv Platform.dc.html:4989`). Uses the repo's own
                hand-authored lucide-style `IconClock` (matching `IconPaperPlane`/
                the ship-plan icons), NOT antd's `ClockCircleOutlined` — the two
                icon sets render visually inconsistent stroke weights/proportions
                side by side, and antd has no glyph matching this exact prototype
                shape (a plain outline clock, no filled ring). */}
            <IconClock />
            Pricing and lead times are locked into this quote for 30 days.
          </p>
        </>
      )}
    </Modal>
  )
}
